import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Skeleton, Alert, Card, CardContent,
  CardActionArea, Chip, Divider
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import {
  MedicalServices, Map as MapIcon, TrendingUp, Assessment,
  AttachMoney, Psychology, AccountTree, People, LocalHospital,
  Schedule, Warning
} from '@mui/icons-material';
import { especializadaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

const COLORS = ['#2563eb', '#16a34a', '#eab308', '#ef4444', '#8b5cf6', '#06b6d4'];

const SITUACAO_LABELS: Record<string, string> = {
  REC: 'Recebida', FIE: 'Fila de Espera', FAL: 'Falta',
  CAN: 'Cancelada', EFE: 'Efetivada', AGE: 'Agendada',
};

const subpaginas = [
  { path: '/especializada/rede', label: 'Rede Especializada', icon: MapIcon, color: '#2563eb', desc: 'Unidades, equipes e geolocalização' },
  { path: '/especializada/prospeccao', label: 'Prospecção Assistencial', icon: TrendingUp, color: '#16a34a', desc: 'Capacidade vs demanda' },
  { path: '/especializada/producao', label: 'Gestão de Produção', icon: Assessment, color: '#7c3aed', desc: 'Produção por unidade e profissional' },
  { path: '/especializada/custos', label: 'Análise de Custos', icon: AttachMoney, color: '#ea580c', desc: 'Planejado vs realizado' },
  { path: '/especializada/predicao', label: 'Predição de Demandas', icon: Psychology, color: '#db2777', desc: 'Projeções com IA' },
  { path: '/especializada/planejamento', label: 'Planejamento Técnico', icon: AccountTree, color: '#0d9488', desc: 'Dados populacionais e epidemiológicos' },
];

const EspecializadaDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    especializadaService.getDashboard(municipioAtivo.id, dataInicio || undefined, dataFim || undefined)
      .then(setData)
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  const dadosContexto = useMemo(() => {
    if (!data) return '';
    return `Municipio: ${municipioAtivo?.nome || 'Contagem/MG'} (685.000 hab)
Periodo: ${dataInicio || 'ultimo ano'} a ${dataFim || 'hoje'}
Total unidades especializadas: ${data.kpis?.total_unidades || 0}
Total profissionais: ${data.kpis?.total_profissionais || 0}
Total atendimentos: ${Number(data.kpis?.total_atendimentos || 0).toLocaleString('pt-BR')}
Total especialidades: ${data.kpis?.total_especialidades || 0}
Fila de espera: ${Number(data.kpis?.total_fila_espera || 0).toLocaleString('pt-BR')}
Custo realizado: R$ ${Number(data.kpis?.custo_total_realizado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
Media dias espera: ${data.kpis?.media_dias_espera || 0}`;
  }, [data, municipioAtivo, dataInicio, dataFim]);

  const regulacaoData = useMemo(() => {
    if (!data?.regulacaoPorSituacao) return [];
    return data.regulacaoPorSituacao.map((r: any) => ({
      name: SITUACAO_LABELS[r.situacao] || r.situacao,
      value: Number(r.total),
      percentual: Number(r.percentual),
    }));
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={400} height={40} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[1,2,3,4].map(i => <Grid item xs={12} sm={6} md={3} key={i}><Skeleton variant="rounded" height={120} /></Grid>)}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Atenção Especializada</Typography>
          <Typography variant="body2" color="text.secondary">
            Monitoramento e Prospecção — {municipioAtivo?.nome || 'Contagem/MG'}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio} dataFim={dataFim}
          onChange={(di, df) => { setDataInicio(di); setDataFim(df); }}
        />
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Unidades Especializadas" valor={data?.kpis?.total_unidades || 0} icone={<LocalHospital />} cor="#2563eb" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Profissionais" valor={data?.kpis?.total_profissionais || 0} icone={<People />} cor="#16a34a" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Total Atendimentos" valor={Number(data?.kpis?.total_atendimentos || 0).toLocaleString('pt-BR')} icone={<MedicalServices />} cor="#7c3aed" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Fila de Espera" valor={Number(data?.kpis?.total_fila_espera || 0).toLocaleString('pt-BR')} icone={<Schedule />} cor="#ef4444" />
        </Grid>
      </Grid>

      {/* Analise IA */}
      <Box sx={{ mb: 3 }}>
        <AnalisePredicaoCard
          manualTrigger={true}
          municipioId={municipioAtivo?.id || 0}
          contexto="dashboard"
          dadosContexto={dadosContexto}
          prompt="Voce e um analista de saude publica. Analise os dados da Atencao Especializada deste municipio e gere 4 secoes: 1) Diagnostico Situacional (resumo geral), 2) Alertas Criticos (pontos de atencao), 3) Acoes Recomendadas (sugestoes praticas), 4) Projecao (tendencias). Seja conciso e objetivo."
        />
      </Box>

      {/* Navegação Sub-páginas */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Módulos</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {subpaginas.map((sp) => {
          const Icon = sp.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={sp.path}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: sp.color, boxShadow: 2 } }}>
                <CardActionArea onClick={() => navigate(sp.path)} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                  <Box sx={{ mr: 2, p: 1, borderRadius: 2, bgcolor: sp.color + '15', color: sp.color }}>
                    <Icon sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{sp.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{sp.desc}</Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={2}>
        {/* Evolução Mensal */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Evolução Mensal de Atendimentos</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={(data?.evolucaoMensal || []).map((d: any) => ({ ...d, mes: new Date(d.mes).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }), total: Number(d.total_atendimentos) }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v: any) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
                <Tooltip formatter={(v: any) => [v.toLocaleString('pt-BR'), 'Atendimentos']} />
                <Line type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Regulação */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Regulação por Situação</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={regulacaoData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} label={({ name, percentual }: any) => `${name}: ${percentual}%`} labelLine={false}>
                  {regulacaoData.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v: any) => v.toLocaleString('pt-BR')} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Unidades */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Top 10 Unidades por Produção</Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={(data?.topUnidades || []).map((u: any) => ({ ...u, nome: (u.nome_unidade || '').substring(0, 30), total: Number(u.total_atendimentos) }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} tickFormatter={(v: any) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
                <YAxis type="category" dataKey="nome" width={200} fontSize={10} />
                <Tooltip formatter={(v: any) => [v.toLocaleString('pt-BR'), 'Atendimentos']} />
                <Bar dataKey="total" fill="#2563eb" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={dadosContexto}
      />
    </Box>
  );
};

export default EspecializadaDashboard;
